import React, { createContext, useContext, useEffect, useMemo, useRef, useState } from "react";

export const TOKEN_KEY = "bank_account_auth_token";

let currentUser = null;
const listeners = new Set();

function notify() {
  listeners.forEach((listener) => listener(currentUser));
}

export function getToken() {
  return localStorage.getItem(TOKEN_KEY);
}

export function getCurrentUser() {
  return currentUser;
}

export function setSession(token, user) {
  localStorage.setItem(TOKEN_KEY, token);
  currentUser = user;
  notify();
}

export function clearBankAccountStorage() {
  Object.keys(localStorage)
    .filter((key) => key.startsWith("bank_account_"))
    .forEach((key) => localStorage.removeItem(key));
}

export function clearSession() {
  clearBankAccountStorage();
  currentUser = null;
  notify();
}

const AuthContext = createContext({
  user: null,
  loading: false,
  restoreError: "",
  retryRestore: () => {},
  establishSession: setSession,
  signOut: clearSession,
});

export function AuthProvider({ children, loadProfile = true }) {
  const [user, setUser] = useState(currentUser);
  const [loading, setLoading] = useState(Boolean(loadProfile && getToken() && !currentUser));
  const [restoreError, setRestoreError] = useState("");
  const [restoreAttempt, setRestoreAttempt] = useState(0);
  const restoreGeneration = useRef(0);

  useEffect(() => {
    const handleUserChange = (nextUser) => {
      restoreGeneration.current += 1;
      setUser(nextUser);
      setLoading(false);
      if (nextUser || !getToken()) {
        setRestoreError("");
      }
    };
    listeners.add(handleUserChange);
    return () => listeners.delete(handleUserChange);
  }, []);

  useEffect(() => {
    if (!loadProfile || !getToken() || currentUser) {
      setLoading(false);
      return undefined;
    }

    let active = true;
    const tokenAtStart = getToken();
    const generationAtStart = restoreGeneration.current + 1;
    restoreGeneration.current = generationAtStart;
    const isCurrentRestore = () =>
      active
      && restoreGeneration.current === generationAtStart
      && getToken() === tokenAtStart;
    setLoading(true);
    setRestoreError("");
    import("../services/apiClient")
      .then(({ apiRequest }) => apiRequest("/api/auth/me"))
      .then((profile) => {
        if (isCurrentRestore()) {
          currentUser = profile;
          setUser(profile);
        }
      })
      .catch((error) => {
        if (isCurrentRestore() && error.status !== 401) {
          setRestoreError("Không thể khôi phục phiên. Thử lại");
        }
      })
      .finally(() => {
        if (isCurrentRestore()) setLoading(false);
      });

    return () => {
      active = false;
    };
  }, [loadProfile, restoreAttempt]);

  const value = useMemo(
    () => ({
      user,
      loading,
      restoreError,
      retryRestore: () => setRestoreAttempt((attempt) => attempt + 1),
      establishSession: setSession,
      signOut: clearSession,
    }),
    [user, loading, restoreError]
  );
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  return useContext(AuthContext);
}
